import { createFileRoute } from '@tanstack/react-router'
import { PlusIcon } from 'lucide-react'
import { useMemo, useState } from 'react'

import type { WorkActivity } from '@/lib/api/work-activity-api'
import { Button } from '@/components/ui/button'
import { Switch } from '@/components/ui/switch'
import { WorkActivityBoard } from '@/components/work-activities/work-activity-board'
import { WorkActivityDialog } from '@/components/work-activities/work-activity-dialog'
import { useAuth } from '@/hooks/use-auth'
import { useWorkActivities } from '@/hooks/use-work-activities-query'
import { m } from '@/lib/paraglide/messages'

/** Số thẻ tối đa giữ lại ở cột "Đóng". */
const CLOSED_COLUMN_LIMIT = 50

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/work-activities/board',
)({
    component: WorkActivityBoardPage,
})

function WorkActivityBoardPage() {
    const { user, hasPermission } = useAuth()
    const [dialogOpen, setDialogOpen] = useState(false)
    const [editing, setEditing] = useState<WorkActivity | null>(null)
    const [onlyMine, setOnlyMine] = useState(false)

    const canReadAll = hasPermission('work_activities.read_all')
    const canClose = hasPermission('work_activities.close')
    const canCreate = hasPermission('work_activities.create')

    // Quản lý có read_all thấy cả công ty; bật công tắc để lọc về việc của
    // chính mình khi bảng quá đông.
    const assigneeID = onlyMine ? user?.id : undefined

    // Ba cột việc đang chạy bị chặn bởi khối lượng việc thật nên tải hết được.
    const active = useWorkActivities({
        type: 'task',
        status: 'open,in_progress,done',
        assignee_id: assigneeID,
    })

    // Cột "Đóng" thì chỉ phình ra theo thời gian và không ai cuộn hết, nên giới
    // hạn ở CLOSED_COLUMN_LIMIT thẻ gần nhất. Không chặn thì sau một năm mở bảng
    // là kéo về vài nghìn thẻ đã xong từ lâu.
    const closed = useWorkActivities({
        type: 'task',
        status: 'closed',
        assignee_id: assigneeID,
        limit: CLOSED_COLUMN_LIMIT,
    })

    const activities = useMemo(
        () => [...(active.data ?? []), ...(closed.data ?? [])],
        [active.data, closed.data],
    )
    const isLoading = active.isLoading || closed.isLoading
    const isError = active.isError || closed.isError

    const openCreate = () => {
        setEditing(null)
        setDialogOpen(true)
    }

    const openEdit = (activity: WorkActivity) => {
        setEditing(activity)
        setDialogOpen(true)
    }

    return (
        <div className="space-y-4 page-pad">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <h1 className="text-lg font-semibold">
                    {m.work_activity_board_title()}
                </h1>

                <div className="flex items-center gap-4">
                    {canReadAll && (
                        <label className="flex items-center gap-2 text-sm">
                            <Switch
                                checked={onlyMine}
                                onCheckedChange={setOnlyMine}
                            />
                            {m.work_activity_board_only_mine()}
                        </label>
                    )}
                    {canCreate && (
                        <Button onClick={openCreate}>
                            <PlusIcon className="size-4" />
                            {m.work_activity_create_button()}
                        </Button>
                    )}
                </div>
            </div>

            {isLoading && (
                <p className="text-muted-foreground text-sm">
                    {m.work_activity_board_loading()}
                </p>
            )}
            {isError && (
                <p className="text-destructive text-sm">
                    {m.work_activity_board_load_error()}
                </p>
            )}

            {!isLoading && !isError && (
                <WorkActivityBoard
                    activities={activities}
                    currentUserID={user?.id}
                    canClose={canClose}
                    onOpen={openEdit}
                />
            )}

            <WorkActivityDialog
                open={dialogOpen}
                onOpenChange={setDialogOpen}
                editing={editing}
                defaultType="task"
                canEdit={!editing || editing.created_by === user?.id}
            />
        </div>
    )
}
